import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../../components/ui/dialog';
import { Button } from '../../../components/ui/button';
import { Label } from '../../../components/ui/label';
import { Checkbox } from '../../../components/ui/checkbox';
import { FileSpreadsheet, Download, Loader2 } from 'lucide-react';
import { EXPORT_FIELD_OPTIONS } from './constants';

export const ExportRosterDialog = ({
  open, onOpenChange, exportTeam, exportFields, setExportFields, loading, onExport
}) => {
  const toggleField = (key) => setExportFields(prev => ({ ...prev, [key]: !prev[key] }));
  const toggleCategory = (category, selected) => {
    const fields = EXPORT_FIELD_OPTIONS.filter(f => f.category === category);
    const updates = {};
    fields.forEach(f => { updates[f.key] = selected; });
    setExportFields(prev => ({ ...prev, ...updates }));
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto" data-testid="export-team-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><FileSpreadsheet className="h-5 w-5 text-emerald-600" />Export Team Roster</DialogTitle>
          <DialogDescription>{exportTeam && (<>Export roster for <strong>{exportTeam.name}</strong> ({exportTeam.player_count || 0} players)</>)}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <p className="text-sm text-slate-600">Select the fields you want to include in the export:</p>
          {['Basic', 'Contact', 'Registration', 'Player Info', 'Uniform', 'Measurements'].map(category => {
            const categoryFields = EXPORT_FIELD_OPTIONS.filter(f => f.category === category);
            const allSelected = categoryFields.every(f => exportFields[f.key]);
            const someSelected = categoryFields.some(f => exportFields[f.key]);
            return (
              <div key={category} className="border rounded-lg p-3">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <Checkbox id={`category-${category}`} checked={allSelected} onCheckedChange={(checked) => toggleCategory(category, checked)} className={someSelected && !allSelected ? 'data-[state=checked]:bg-slate-400' : ''} data-testid={`export-category-${category.toLowerCase().replace(' ', '-')}`} />
                    <Label htmlFor={`category-${category}`} className="font-medium cursor-pointer">{category}</Label>
                  </div>
                  <span className="text-xs text-slate-400">{categoryFields.filter(f => exportFields[f.key]).length}/{categoryFields.length} selected</span>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2 ml-6">
                  {categoryFields.map(field => (
                    <div key={field.key} className="flex items-center gap-2">
                      <Checkbox id={`field-${field.key}`} checked={exportFields[field.key]} onCheckedChange={() => toggleField(field.key)} data-testid={`export-field-${field.key}`} />
                      <Label htmlFor={`field-${field.key}`} className="text-sm cursor-pointer">{field.label}</Label>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
          <div className="flex gap-2 pt-2 border-t">
            <Button variant="outline" size="sm" onClick={() => { const all = {}; EXPORT_FIELD_OPTIONS.forEach(f => { all[f.key] = true; }); setExportFields(all); }} data-testid="export-select-all">Select All</Button>
            <Button variant="outline" size="sm" onClick={() => { const none = {}; EXPORT_FIELD_OPTIONS.forEach(f => { none[f.key] = false; }); setExportFields(none); }} data-testid="export-deselect-all">Deselect All</Button>
            <Button variant="outline" size="sm" onClick={() => setExportFields({ player_name: true, date_of_birth: true, age: true, gender: false, parent_name: true, parent_email: true, parent_phone: true, payment_status: true, skill_level: false, cricclubs_id: false, cricclubs_email: false, shirt_name: false, shirt_number: false, chest_size: false, shirt_length: false, waist_size: false, trouser_length: false })} data-testid="export-reset-default">Reset to Default</Button>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={loading}>Cancel</Button>
          <Button onClick={onExport} disabled={loading || Object.values(exportFields).every(v => !v)} className="bg-emerald-600 hover:bg-emerald-700" data-testid="export-team-submit">
            {loading ? (<><Loader2 className="h-4 w-4 mr-2 animate-spin" />Exporting...</>) : (<><Download className="h-4 w-4 mr-2" />Export to CSV</>)}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
